import { useMemo } from 'react'
import moment from 'moment'

export default function MicroVolumeCompletionTimelineDisplay({
    clientDate = null,
    mesoVolumes = [],
    renderRangeStart,
    renderRangeEnd,
    daySpacing,
    DateToTrackLeftX = () => { return 0 },
    DateToWeekIndex = () => { return 0 },
    HandleMicroVolumeClick = () => {},
}) {
    const microVolumeCompletionStatuses = useMemo(() => {
        if (!mesoVolumes?.length || daySpacing <= 0) {
            return []
        }

        const clientDateMoment = clientDate ? moment(clientDate, 'YYYY-MM-DD') : null
        const rings = []
        let programWeekIndex = 0

        mesoVolumes.forEach((mesoVolume) => {

            const microVolumes = mesoVolume.microVolumes ?? []

            microVolumes.forEach((microVolume) => {

                const startDate = moment(microVolume.startDate).startOf('day')
                const endDate = moment(microVolume.endDate).startOf('day')

                if (
                    !startDate.isValid()
                    || !endDate.isValid()
                    || endDate.isBefore(renderRangeStart, 'day')
                    || startDate.isAfter(renderRangeEnd, 'day')
                ) {
                    programWeekIndex += 1
                    return
                }

                const left = DateToTrackLeftX(startDate)
                const width = DateToTrackLeftX(endDate.clone().add(1, 'day')) - left

                const isMicroVolumeBeforeClientISOWeek = clientDateMoment ? startDate.isBefore(clientDateMoment, 'isoWeek') : false
                const isMicroVolumeInClientISOWeek = clientDateMoment ? startDate.isSame(clientDateMoment, 'isoWeek') : false
                const isMicroVolumeAfterClientISOWeek = clientDateMoment ? startDate.isAfter(clientDateMoment, 'isoWeek') : false

                rings.push({
                    key: microVolume.microVolumeId ?? microVolume.weekOfTheYearKey ?? `program-week-${programWeekIndex}`,
                    left,
                    width,
                    weekIndex: DateToWeekIndex(startDate),
                    microVolumeConfig: microVolume,
                    strengthWorkoutCompletionPercentage: microVolume.strengthWorkoutCompletionPercentage,
                    isMicroVolumeBeforeClientISOWeek,
                    isMicroVolumeInClientISOWeek,
                    isMicroVolumeAfterClientISOWeek,
                })

                programWeekIndex += 1

            })
        })

        return rings
    }, [
        clientDate,
        renderRangeStart,
        renderRangeEnd,
        mesoVolumes,
        daySpacing,
        DateToTrackLeftX,
        DateToWeekIndex,
    ])

    return (
        <div className='relative h-4 2xl:h-5'>
            {microVolumeCompletionStatuses.map((microVolumeCompletionStatus) => {
                const strengthWorkoutCompletionPercentage = microVolumeCompletionStatus?.strengthWorkoutCompletionPercentage ?? 0
                var completionKey = ''

                // Past

                if (microVolumeCompletionStatus.isMicroVolumeBeforeClientISOWeek) {

                    if (strengthWorkoutCompletionPercentage < 50) completionKey = 'incomplete'

                    else if (50 <= strengthWorkoutCompletionPercentage && strengthWorkoutCompletionPercentage < 100) completionKey = 'half-complete'

                    else if (100 <= strengthWorkoutCompletionPercentage) completionKey = 'complete'

                }

                // Present and future

                if (microVolumeCompletionStatus.isMicroVolumeInClientISOWeek || microVolumeCompletionStatus.isMicroVolumeAfterClientISOWeek) {
                    completionKey = 'future'
                }

                return (
                    <div
                        key={microVolumeCompletionStatus.key}
                        className='absolute inset-y-0 flex items-center justify-center cursor-pointer pointer-events-auto'
                        style={{
                            left: `${microVolumeCompletionStatus.left}px`,
                            width: `${microVolumeCompletionStatus.width}px`,
                        }}
                        title={`${microVolumeCompletionStatus.strengthWorkoutCompletionPercentage}% of strength workouts completed`}
                        onClick={() => HandleMicroVolumeClick(microVolumeCompletionStatus)}
                    >
                        {completionKey === 'incomplete' && (
                            <svg
                                className='w-4 h-4 2xl:w-5 2xl:h-5'
                                style={{ display: 'block' }}
                                viewBox="0 0 20 20"
                            >
                                <circle cx="10" cy="10" r="8.5" fill="transparent" stroke="#fb7185" strokeWidth="2.5"/> {/* Outer red ring */}
                                <circle cx="10" cy="10" r="5" fill="#fb7185"/> {/* Inner red circle */}
                            </svg>
                        )}

                        {completionKey === 'half-complete' && (
                            <svg
                                className='w-4 h-4 2xl:w-5 2xl:h-5'
                                style={{ display: 'block' }}
                                viewBox="0 0 20 20"
                            >
                                <circle cx="10" cy="10" r="8.5" fill="transparent" stroke="#facc15" strokeWidth="2.5"/> {/* Outer yellow ring */}
                                <circle cx="10" cy="10" r="5" fill="#facc15"/> {/* Inner yellow circle */}
                            </svg>
                        )}

                        {completionKey === 'complete' && (
                            <svg
                                className='w-4 h-4 2xl:w-5 2xl:h-5'
                                style={{ display: 'block' }}
                                viewBox="0 0 20 20"
                            >
                                <circle cx="10" cy="10" r="8.5" fill="transparent" stroke="#4ade80" strokeWidth="2.5"/> {/* Outer green ring */}
                                <circle cx="10" cy="10" r="5" fill="#4ade80"/> {/* Inner green circle */}
                            </svg>
                        )}

                        {/* TODO: Bring back support for skipped */}
                        {/* 
                        {weekCompletionStatusIconKey === 'active-skipped' && 
                            <ForwardIcon className="-my-0.5 -ml-[2px] w-6 h-6 text-gray-400" />
                        }
                        */}

                        {completionKey === 'future' && (
                            <svg
                                className='w-4 h-4 2xl:w-5 2xl:h-5'
                                style={{ display: 'block' }}
                                viewBox="0 0 20 20"
                            >
                                <circle cx="10" cy="10" r="8.5" fill="transparent" stroke="#99a1af" strokeWidth="2.5"/>
                            </svg>
                        )}
                    </div>
                )
            })}
        </div>
    )
}
